import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, CircularProgress, IconButton, TextField, Alert,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, MenuItem, Divider
} from '@mui/material';
import { ToggleOn, ToggleOff, Add, Edit, Delete } from '@mui/icons-material';

const AdminUsers = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusUpdatingId, setStatusUpdatingId] = useState('');
  const [pageMessage, setPageMessage] = useState({ type: '', text: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [deleteLoadingId, setDeleteLoadingId] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    role: 'customer',
    supplierId: ''
  });
  const [aiUsage, setAiUsage] = useState(null);
  const [aiUsageLoading, setAiUsageLoading] = useState(false);
  const [aiOverrideInput, setAiOverrideInput] = useState('');
  const [aiSaving, setAiSaving] = useState(false);

  useEffect(() => {
    loadUsers();
    loadSuppliers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users');
      setUsers(data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadSuppliers = async () => {
    try {
      const { data } = await api.get('/suppliers');
      setSuppliers(data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const openAdd = () => {
    setSelectedUser(null);
    setFormData({ firstName: '', lastName: '', email: '', password: '', phone: '', role: 'customer', supplierId: '' });
    setAiUsage(null);
    setAiOverrideInput('');
    setDialogOpen(true);
  };

  const loadAiUsage = async (userId) => {
    setAiUsageLoading(true);
    try {
      const { data } = await api.get(`/admin/ai-usage/${userId}`);
      setAiUsage(data.data);
      setAiOverrideInput(typeof data.data.manualDailyBudgetUSD === 'number' ? String(data.data.manualDailyBudgetUSD) : '');
    } catch (err) {
      console.error(err);
    }
    setAiUsageLoading(false);
  };

  const openEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      password: '',
      phone: user.phone || '',
      role: user.role || 'customer',
      supplierId: user.supplierId?._id || user.supplierId || ''
    });
    setAiUsage(null);
    setAiOverrideInput('');
    setDialogOpen(true);
    loadAiUsage(user._id);
  };

  const handleSaveOverride = async () => {
    if (!selectedUser) return;
    const value = parseFloat(aiOverrideInput);
    if (Number.isNaN(value) || value < 0) {
      setPageMessage({ type: 'error', text: 'Enter a valid, non-negative daily budget.' });
      return;
    }
    setAiSaving(true);
    try {
      await api.put(`/admin/ai-usage/${selectedUser._id}/override`, { dailyBudgetUSD: value });
      await loadAiUsage(selectedUser._id);
      setPageMessage({ type: 'success', text: "AI assistant daily budget updated." });
    } catch (err) {
      setPageMessage({ type: 'error', text: err.response?.data?.error || 'Failed to update the daily budget.' });
    }
    setAiSaving(false);
  };

  const handleResetOverride = async () => {
    if (!selectedUser) return;
    setAiSaving(true);
    try {
      await api.delete(`/admin/ai-usage/${selectedUser._id}/override`);
      await loadAiUsage(selectedUser._id);
      setPageMessage({ type: 'success', text: 'AI assistant daily budget reset to the calculated value.' });
    } catch (err) {
      setPageMessage({ type: 'error', text: err.response?.data?.error || 'Failed to reset the daily budget.' });
    }
    setAiSaving(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...formData };
      if (payload.role !== 'supplier_employee') {
        payload.supplierId = '';
      }
      if (!payload.password) {
        delete payload.password;
      }

      if (selectedUser) {
        await api.put(`/users/${selectedUser._id}`, payload);
      } else {
        await api.post('/users', payload);
      }

      setPageMessage({
        type: 'success',
        text: selectedUser ? 'User updated successfully.' : 'User created successfully.'
      });
      setDialogOpen(false);
      loadUsers();
      loadSuppliers();
    } catch (err) {
      console.error(err);
      setPageMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save user.' });
    }
    setSaving(false);
  };

  const handleToggleStatus = async (user) => {
    setStatusUpdatingId(user._id);
    try {
      await api.put(`/users/${user._id}/status`, { isActive: !user.isActive });
      setPageMessage({
        type: 'success',
        text: !user.isActive ? 'User activated successfully.' : 'User deactivated successfully.'
      });
      loadUsers();
    } catch (err) {
      console.error(err);
      setPageMessage({ type: 'error', text: err.response?.data?.error || 'Failed to update user status.' });
    }
    setStatusUpdatingId('');
  };

  const openDeleteDialog = (user) => {
    setDeleteDialog({ open: true, user });
  };

  const closeDeleteDialog = () => {
    if (deleteLoadingId) return;
    setDeleteDialog({ open: false, user: null });
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog.user) return;

    setDeleteLoadingId(deleteDialog.user._id);
    try {
      await api.delete(`/users/${deleteDialog.user._id}`);
      setPageMessage({ type: 'success', text: 'User deleted successfully.' });
      setDeleteDialog({ open: false, user: null });
      loadUsers();
      loadSuppliers();
    } catch (err) {
      console.error(err);
      setPageMessage({ type: 'error', text: err.response?.data?.error || 'Failed to delete user.' });
    }
    setDeleteLoadingId('');
  };

  const filtered = users.filter(u =>
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.firstName?.toLowerCase().includes(search.toLowerCase()) ||
    u.lastName?.toLowerCase().includes(search.toLowerCase()) ||
    u.supplierId?.companyName?.toLowerCase().includes(search.toLowerCase())
  );

  const roleColors = { admin: 'error', supplier_employee: 'primary', customer: 'success' };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>{t('manage_users')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd}>Add User</Button>
      </Box>

      {pageMessage.text && (
        <Alert severity={pageMessage.type || 'info'} sx={{ mb: 2 }} onClose={() => setPageMessage({ type: '', text: '' })}>
          {pageMessage.text}
        </Alert>
      )}

      <TextField fullWidth placeholder="Search users..." value={search}
        onChange={(e) => setSearch(e.target.value)} sx={{ mb: 3 }} size="small" />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Customer ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((user) => (
              <TableRow key={user._id} hover>
                <TableCell>{user.firstName} {user.lastName}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip label={user.role} size="small" color={roleColors[user.role] || 'default'} />
                </TableCell>
                <TableCell>{user.supplierId?.companyName || '-'}</TableCell>
                <TableCell>{user.customerId || '-'}</TableCell>
                <TableCell>
                  <Chip label={user.isActive ? 'Active' : 'Inactive'} size="small"
                    color={user.isActive ? 'success' : 'error'} />
                </TableCell>
                <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => openEdit(user)} title="Edit user">
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => openDeleteDialog(user)}
                    disabled={deleteLoadingId === user._id}
                    title="Delete user"
                  >
                    {deleteLoadingId === user._id ? <CircularProgress size={18} /> : <Delete />}
                  </IconButton>
                  <IconButton
                    size="small"
                    color={user.isActive ? 'warning' : 'success'}
                    onClick={() => handleToggleStatus(user)}
                    disabled={statusUpdatingId === user._id}
                    title={user.isActive ? 'Deactivate user' : 'Activate user'}
                  >
                    {statusUpdatingId === user._id
                      ? <CircularProgress size={18} />
                      : user.isActive
                        ? <ToggleOff />
                        : <ToggleOn />}
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedUser ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="First Name" value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Last Name" value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Email" value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label={selectedUser ? 'Password (leave blank to keep current)' : 'Password'} type="password" value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })} sx={{ mt: 2 }} />
          <TextField fullWidth label="Phone" value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })} sx={{ mt: 2 }} />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            >
              <MenuItem value="customer">Customer</MenuItem>
              <MenuItem value="supplier_employee">Supplier User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          {formData.role === 'supplier_employee' && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Supplier</InputLabel>
              <Select
                label="Supplier"
                value={formData.supplierId}
                onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
              >
                {suppliers.map((supplier) => (
                  <MenuItem key={supplier._id} value={supplier._id}>{supplier.companyName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {selectedUser && (
            <>
              <Divider sx={{ mt: 3, mb: 2 }} />
              <Typography variant="subtitle2" fontWeight={700}>AI Assistant Daily Budget</Typography>
              {aiUsageLoading ? (
                <Box display="flex" justifyContent="center" py={2}><CircularProgress size={22} /></Box>
              ) : aiUsage ? (
                <>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1, mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Free days used: {aiUsage.freeDaysUsed}/{aiUsage.freeDaysLimit}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active purchase bonuses: {aiUsage.activePurchaseBonuses?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total confirmed paid: ${aiUsage.totalConfirmedPaidUSD?.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Spent today: ${aiUsage.todaySpendUSD?.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Calculated budget: ${aiUsage.calculatedDailyBudgetUSD?.toFixed(2)}/day
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      label="Manual daily budget override (USD)"
                      type="number"
                      size="small"
                      value={aiOverrideInput}
                      onChange={(e) => setAiOverrideInput(e.target.value)}
                      helperText={aiUsage.manualDailyBudgetUSD !== null
                        ? `Override active — calculated value is $${aiUsage.calculatedDailyBudgetUSD?.toFixed(2)}/day`
                        : 'No override — using the calculated value above'}
                      inputProps={{ min: 0, step: 0.5 }}
                      sx={{ minWidth: 260 }}
                    />
                    <Button variant="contained" size="small" onClick={handleSaveOverride} disabled={aiSaving} sx={{ mt: 0.3 }}>
                      {aiSaving ? <CircularProgress size={16} /> : 'Save'}
                    </Button>
                    <Button
                      variant="outlined" size="small" onClick={handleResetOverride}
                      disabled={aiSaving || aiUsage.manualDailyBudgetUSD === null}
                      sx={{ mt: 0.3 }}
                    >
                      Reset to calculated
                    </Button>
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">Could not load AI usage data.</Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog.open} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Delete {deleteDialog.user?.firstName} {deleteDialog.user?.lastName} permanently from the user list.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={Boolean(deleteLoadingId)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteUser}
            disabled={!deleteDialog.user || Boolean(deleteLoadingId)}
          >
            {deleteLoadingId ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUsers;
